import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import { ActionMenu, type ActionMenuItem, SyutGrid, type SyutGridColumn, MinimizedModalStrip } from 'components/ui';
import type { ActionButton, ViewModeOption } from 'components/modals/common/ModuleHeader';
import notificationService from '../../../services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { moduleService } from 'api/services';
import type { ModuleDto, CreateModuleRequest, UpdateModuleRequest } from 'api/types';

// ============================================================================
// MODULE FORM MODAL
// ============================================================================

interface ModuleFormModalProps {
  open: boolean;
  mode: 'create' | 'edit' | 'copy';
  record: ModuleDto | null;
  onClose: () => void;
  onSave: () => void;
  tabId?: string;
  moduleName?: string;
  instanceId?: string;
  minimizeRef?: React.MutableRefObject<(() => void) | null>;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy', instanceId?: string, recordCode?: string) => void;
}

const ModuleFormModal: React.FC<ModuleFormModalProps> = ({ open, mode, record, onClose, onSave, tabId, moduleName, instanceId, minimizeRef, onRestoreModal }) => {
  const { minimize } = useModalMinimize();
  const [formData, setFormData] = useState({ moduleCode: '', moduleName: '', description: '', iconKey: '', sortOrder: 0 });
  const [saving, setSaving] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const savedSnapshot = useRef<string | null>(null);

  // Refs for minimize/restore — captures current state in closure
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const isRestoreRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    if (mode === 'edit' && record) {
      const fd = { moduleCode: record.moduleCode, moduleName: record.moduleName, description: record.description || '', iconKey: record.iconKey || '', sortOrder: record.sortOrder };
      setFormData(fd);
      savedSnapshot.current = JSON.stringify(fd);
    } else if (mode === 'copy' && record) {
      const fd = { moduleCode: '', moduleName: record.moduleName + ' (Copy)', description: record.description || '', iconKey: record.iconKey || '', sortOrder: record.sortOrder };
      setFormData(fd);
      savedSnapshot.current = JSON.stringify(fd);
    } else {
      const fd = { moduleCode: '', moduleName: '', description: '', iconKey: '', sortOrder: 0 };
      setFormData(fd);
      savedSnapshot.current = JSON.stringify(fd);
    }
    setFieldErrors({});
  }, [open, mode, record]);

  const handleMinimize = useCallback(() => {
    if (!tabId) return;
    const currentFormData = { ...formDataRef.current };
    const currentSnapshot = savedSnapshot.current || '';
    const currentIsDirty = currentSnapshot ? JSON.stringify(currentFormData) !== currentSnapshot : false;
    const idx = instanceId || '1';
    let title: string;
    if (mode === 'create' || mode === 'copy') {
      const prefix = mode === 'copy' ? 'Copy' : 'Add';
      title = currentFormData.moduleCode ? `${prefix} - ${currentFormData.moduleCode} (${idx})` : currentFormData.moduleName ? `${prefix} - ${currentFormData.moduleName} (${idx})` : `${prefix} New (${idx})`;
    } else {
      title = `Edit - ${record?.moduleCode || currentFormData.moduleCode}`;
    }
    minimize({
      id: tabId ? `${tabId}-module-modal-${mode === 'edit' ? record?.id : mode + '-' + instanceId}` : 'module-modal',
      tabId: tabId!,
      moduleName: moduleName || 'Module Master',
      title,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(currentFormData);
        savedSnapshot.current = currentSnapshot;
        onRestoreModal?.(mode, instanceId, mode !== 'create' ? record?.moduleCode : undefined);
      },
    });
    onClose();
  }, [tabId, instanceId, mode, record, minimize, moduleName, onRestoreModal, onClose]);

  useEffect(() => { if (minimizeRef) minimizeRef.current = handleMinimize; }, [minimizeRef, handleMinimize]);

  const handleClose = useCallback(() => {
    const isDirty = savedSnapshot.current ? JSON.stringify(formData) !== savedSnapshot.current : false;
    if (isDirty) {
      setCloseConfirmOpen(true);
    } else {
      onClose();
    }
  }, [formData, onClose]);

  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    if (!formData.moduleCode.trim()) errors.moduleCode = 'Module Code is required';
    if (!formData.moduleName.trim()) errors.moduleName = 'Module Name is required';
    if (Object.keys(errors).length > 0) { setFieldErrors(errors); return; }
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setSaving(true);
    try {
      if (mode !== 'edit') {
        try {
          const available = await moduleService.isModuleCodeAvailable(formData.moduleCode, undefined);
          if (!available) {
            setSaving(false);
            setFieldErrors({ moduleCode: 'This code already exists. Please use a different code.' });
            return;
          }
        } catch {
          // API unavailable — let server enforce uniqueness on create
        }
      }

      if (mode === 'edit' && record) {
        const req: UpdateModuleRequest = { moduleCode: formData.moduleCode.toUpperCase(), moduleName: formData.moduleName, description: formData.description || undefined, iconKey: formData.iconKey || undefined, sortOrder: formData.sortOrder, isActive: true, rowVersion: record.rowVersion };
        await moduleService.updateModule(record.id, req);
        notificationService.toast.success(`Module "${formData.moduleName}" has been updated successfully.`);
      } else {
        const req: CreateModuleRequest = { moduleCode: formData.moduleCode.toUpperCase(), moduleName: formData.moduleName, description: formData.description || undefined, iconKey: formData.iconKey || undefined, sortOrder: formData.sortOrder, isActive: true };
        await moduleService.createModule(req);
        notificationService.toast.success(`Module "${formData.moduleName}" has been created successfully.`);
      }
      onSave();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'An error occurred while saving. Please try again.';
      notificationService.toast.error(msg);
    } finally {
      setSaving(false);
    }
  }, [formData, mode, record, onSave]);

  if (!open) return null;
  const title = mode === 'create' ? 'Add Module' : mode === 'copy' ? 'Copy Module' : `Edit Module — ${record?.moduleCode}`;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={saving}><IconSystem.chevronDown size={16} /></button>}
            <button className="modal-close" onClick={handleClose} disabled={saving}><IconSystem.x size={20} /></button>
          </div>
        </div>
        <div className="modal-content">
          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">Module Code <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${fieldErrors.moduleCode ? ' form-field-error' : ''}`}
                value={formData.moduleCode}
                onChange={e => { setFormData(p => ({ ...p, moduleCode: e.target.value.toUpperCase() })); setFieldErrors(prev => { const { moduleCode, ...rest } = prev; return rest; }); }}
                placeholder="e.g., IAM"
                readOnly={mode === 'edit'}
                style={mode === 'edit' ? { background: 'var(--bg-secondary)' } : {}}
                maxLength={50}
              />
              {fieldErrors.moduleCode && <div className="form-error-message">{fieldErrors.moduleCode}</div>}
            </div>
            <div className="form-group">
              <label className="form-label">Module Name <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${fieldErrors.moduleName ? ' form-field-error' : ''}`}
                value={formData.moduleName}
                onChange={e => { setFormData(p => ({ ...p, moduleName: e.target.value })); setFieldErrors(prev => { const { moduleName, ...rest } = prev; return rest; }); }}
                placeholder="e.g., Identity & Access Management"
                maxLength={150}
              />
              {fieldErrors.moduleName && <div className="form-error-message">{fieldErrors.moduleName}</div>}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea className="form-textarea" value={formData.description} onChange={e => setFormData(p => ({ ...p, description: e.target.value }))} rows={2} maxLength={500} placeholder="Module description" />
          </div>
          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">Icon Key</label>
              <input type="text" className="form-input" value={formData.iconKey} onChange={e => setFormData(p => ({ ...p, iconKey: e.target.value }))} maxLength={100} placeholder="e.g. shield, settings" />
            </div>
            <div className="form-group">
              <label className="form-label">Sort Order</label>
              <input type="number" className="form-input" value={formData.sortOrder} onChange={e => setFormData(p => ({ ...p, sortOrder: parseInt(e.target.value) || 0 }))} />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={handleClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : mode === 'edit' ? 'Save Changes' : 'Create Module'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'edit' ? 'Confirm Update' : 'Confirm Create'}
        message={mode === 'edit' ? `Save changes to module "${formData.moduleName}"?` : `Create module "${formData.moduleName}"?`}
        confirmLabel={mode === 'edit' ? 'Save' : 'Create'}
        cancelLabel="Cancel"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={closeConfirmOpen}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to close? Your changes will be lost."
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        onConfirm={() => { setCloseConfirmOpen(false); onClose(); }}
        onCancel={() => setCloseConfirmOpen(false)}
        variant="danger"
      />
    </>
  );
};

// ============================================================================
// MODULE MASTER SCREEN
// ============================================================================

interface ModuleMasterScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

type GridRecord = Omit<ModuleDto, 'id'> & { id: string };

const ModuleMasterScreen: React.FC<ModuleMasterScreenProps> = ({ moduleIcon, tabId, moduleName }) => {
  const { isMinimized, dismiss: dismissMinimized } = useModalMinimize();

  // View & filter state
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [filterOpen, setFilterOpen] = useState(false);

  // Grid state
  const [queryParams, setQueryParams] = useState<{ page: number; pageSize: number; searchTerm?: string }>({ page: 1, pageSize: 25 });
  const [data, setData] = useState<ModuleDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  // Modal state
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy'>('create');
  const [selectedRecord, setSelectedRecord] = useState<ModuleDto | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState('0');
  const copyInstanceCounter = useRef(0);
  const [copyInstanceId, setCopyInstanceId] = useState('0');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  // Load data — silent=true skips loading spinner
  const loadData = useCallback(async (params: typeof queryParams, silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await moduleService.getAllModules(params);
      setData(res.data);
      setTotalCount(res.pagination.totalItems);
    } catch {
      notificationService.toast.error('Failed to load modules');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(queryParams); }, [queryParams, loadData]);

  useEffect(() => {
    const t = setTimeout(() => setQueryParams(p => ({ ...p, searchTerm: searchQuery || undefined, page: 1 })), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const refetch = useCallback(() => loadData(queryParams, true), [loadData, queryParams]);

  const handleSaveSuccess = useCallback(() => { setFormModalOpen(false); setSelectedRecord(null); refetch(); }, [refetch]);

  // CRUD handlers
  const handleAdd = useCallback(() => {
    if (formModalOpen && formModalMinimizeRef.current) formModalMinimizeRef.current();
    createInstanceCounter.current += 1;
    setCreateInstanceId(String(createInstanceCounter.current));
    setSelectedRecord(null);
    setFormMode('create');
    setFormModalOpen(true);
  }, [formModalOpen]);

  const handleEdit = useCallback((mod: ModuleDto) => {
    const modalId = `${tabId}-module-modal-${mod.id}`;
    if (tabId && isMinimized(modalId)) { dismissMinimized(modalId); setSelectedRecord(mod); setFormMode('edit'); setFormModalOpen(true); return; }
    if (formModalOpen && formModalMinimizeRef.current) formModalMinimizeRef.current();
    setSelectedRecord(mod);
    setFormMode('edit');
    setFormModalOpen(true);
  }, [formModalOpen, tabId, isMinimized, dismissMinimized]);

  const handleCopy = useCallback((mod: ModuleDto) => {
    if (formModalOpen && formModalMinimizeRef.current) formModalMinimizeRef.current();
    copyInstanceCounter.current += 1;
    setCopyInstanceId(String(copyInstanceCounter.current));
    setSelectedRecord(mod);
    setFormMode('copy');
    setFormModalOpen(true);
  }, [formModalOpen]);

  const handleDeleteRequest = useCallback((mod: ModuleDto) => { setSelectedRecord(mod); setDeleteModalOpen(true); }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedRecord) return;
    setIsDeleting(true);
    try {
      await moduleService.deleteModule(selectedRecord.id);
      notificationService.toast.success(`Module "${selectedRecord.moduleName}" has been deleted successfully.`);
      setDeleteModalOpen(false);
      setSelectedRecord(null);
      refetch();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Failed to delete module');
    } finally {
      setIsDeleting(false);
    }
  }, [selectedRecord, refetch]);

  const handleRestoreModal = useCallback((mode: 'create' | 'edit' | 'copy', instId?: string, code?: string) => {
    if (instId) { if (mode === 'copy') setCopyInstanceId(instId); else setCreateInstanceId(instId); }
    if (code) { const rec = data.find(r => r.moduleCode === code); if (rec) setSelectedRecord(rec); }
    setFormMode(mode);
    setFormModalOpen(true);
  }, [data]);

  // Grid columns
  const columns: SyutGridColumn<GridRecord>[] = useMemo(() => [
    { id: 'moduleCode', header: 'Code', accessorKey: 'moduleCode', size: 120, cell: ({ row }: any) => <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{row.original.moduleCode}</span> },
    { id: 'moduleName', header: 'Module Name', accessorKey: 'moduleName', size: 220 },
    { id: 'description', header: 'Description', accessorKey: 'description', size: 250 },
    { id: 'iconKey', header: 'Icon', accessorKey: 'iconKey', size: 100, cell: ({ row }: any) => <span style={{ fontFamily: 'monospace', color: 'var(--text-secondary)' }}>{row.original.iconKey || '—'}</span> },
    { id: 'sortOrder', header: 'Order', accessorKey: 'sortOrder', size: 70, cell: ({ row }: any) => <span style={{ textAlign: 'right', display: 'block' }}>{row.original.sortOrder}</span> },
    { id: 'menuCount', header: 'Menus', accessorKey: 'menuCount', size: 70, cell: ({ row }: any) => <span style={{ textAlign: 'right', display: 'block' }}>{row.original.menuCount ?? 0}</span> },
    {
      id: 'actions', header: 'Actions', size: 70,
      cell: ({ row }: any) => {
        const m = row.original as ModuleDto;
        const items: ActionMenuItem[] = [
          { id: 'edit', label: 'Edit', icon: <IconSystem.edit size={14} />, onClick: () => handleEdit(m) },
          { id: 'copy', label: 'Copy', icon: <IconSystem.copy size={14} />, onClick: () => handleCopy(m) },
          { id: 'delete', label: 'Delete', icon: <IconSystem.trash size={14} />, onClick: () => handleDeleteRequest(m), variant: 'danger' },
        ];
        return <ActionMenu items={items} />;
      },
    },
  ], [handleEdit, handleCopy, handleDeleteRequest]);

  const gridData = data as unknown as GridRecord[];

  // View mode options
  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View (Not Applicable)', disabled: true },
    { id: 'calendar', icon: <IconSystem.calendar size={16} />, label: 'Calendar View', tooltip: 'Calendar View (Not Applicable)', disabled: true },
  ];

  // Action buttons
  const actionButtons: ActionButton[] = [
    { id: 'add', icon: <IconSystem.add size={18} />, label: 'Add Module', onClick: handleAdd, tooltip: 'Add New Module', variant: 'icon' },
    { id: 'refresh', icon: <IconSystem.refresh size={18} />, label: 'Refresh', onClick: refetch, tooltip: 'Refresh', variant: 'icon' },
    { id: 'export', icon: <IconSystem.download size={18} />, label: 'Export', onClick: () => {}, tooltip: 'Export', variant: 'icon' },
  ];

  return (
    <div className="module-master-container">
      <ModuleHeader
        visible={true}
        title="Module Master"
        description="Manage application modules"
        icon={moduleIcon && (IconSystem as any)[moduleIcon]
          ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
          : undefined
        }
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search modules...' }}
        viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (mode) => setViewMode(mode as 'list' | 'grid') }}
        filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div className="data-grid-container">
          <SyutGrid
            data={gridData}
            columns={columns}
            isLoading={loading}
            manualPagination
            totalCount={totalCount}
            currentPage={(queryParams.page ?? 1) - 1}
            pageSize={queryParams.pageSize ?? 25}
            onPageChange={idx => setQueryParams(p => ({ ...p, page: idx + 1 }))}
            onPageSizeChange={sz => setQueryParams(p => ({ ...p, pageSize: sz, page: 1 }))}
            emptyMessage="No modules found"
          />
        </div>
      </div>

      <ModuleFormModal
        open={formModalOpen}
        mode={formMode}
        record={selectedRecord}
        onClose={() => { setFormModalOpen(false); setSelectedRecord(null); }}
        onSave={handleSaveSuccess}
        tabId={tabId}
        moduleName={moduleName}
        instanceId={formMode === 'copy' ? copyInstanceId : createInstanceId}
        minimizeRef={formModalMinimizeRef}
        onRestoreModal={handleRestoreModal}
      />

      <ConfirmDialog
        open={deleteModalOpen}
        title="Delete Module"
        message={`Delete module "${selectedRecord?.moduleName}"? All associated menus will also be removed. This cannot be undone.`}
        confirmLabel={isDeleting ? 'Deleting...' : 'Delete'}
        cancelLabel="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={() => { setDeleteModalOpen(false); setSelectedRecord(null); }}
        variant="danger"
      />
    </div>
  );
};

export default ModuleMasterScreen;
